import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const esc = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const dash = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const formatAddress = (parts) =>
  parts
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");

export function buildPackageLabelPayload(order, options = {}) {
  const products = Array.isArray(options.products) ? options.products : [];
  const items = products.map((p) => ({
    name: p.productName || p.title || p.name || `Product #${p.productId || ""}`,
    qty: Number(p.quantity || p.qty || 1),
  }));

  const deliveryMethod = String(order?.deliveryMethod || "").toLowerCase();
  const orderId = order?.id;

  return {
    orderId: String(orderId),
    barcodeValue: `JCGN-${orderId}`,
    qrPayload: JSON.stringify({ t: "jcgn-order", id: Number(orderId), v: 1 }),
    deliveryLabel:
      deliveryMethod === "pickup"
        ? "PICKUP"
        : deliveryMethod === "delivery"
          ? "DELIVERY"
          : "ORDER",
    printedAt: new Date().toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    shipTo: {
      name: order.contactname || options.customerName || "",
      phone: order.mobilenumber || "",
      line1: order.line1 || "",
      line2: order.line2 || "",
      city: order.city || "",
      state: order.state || "",
      zip: order.zipCode || "",
      country: order.country || "",
    },
    shipFrom: {
      name: options.sellerName || "JCGN Marketplace Seller",
      phone: options.sellerPhone || "",
      location: options.sellerLocation || "",
    },
    items,
    itemCount: items.length
      ? items.reduce((sum, item) => sum + item.qty, 0)
      : Number(options.itemCount || 0),
  };
}

async function renderBarcodeDataUrl(value) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format: "CODE128",
    displayValue: true,
    fontSize: 14,
    height: 56,
    margin: 8,
    background: "#ffffff",
    lineColor: "#111111",
  });
  return canvas.toDataURL("image/png");
}

async function renderQrDataUrl(value) {
  return QRCode.toDataURL(value, {
    width: 140,
    margin: 1,
    errorCorrectionLevel: "M",
  });
}

function buildLabelHtml(payload, barcodeDataUrl, qrDataUrl) {
  const itemRows = payload.items
    .slice(0, 8)
    .map(
      (item) =>
        `<tr><td>${esc(item.name)}</td><td class="qty">×${esc(item.qty)}</td></tr>`
    )
    .join("");

  const moreItems =
    payload.items.length > 8
      ? `<tr><td colspan="2" class="muted">+${payload.items.length - 8} more item(s)</td></tr>`
      : "";

  const contentsFallback =
    !payload.items.length && payload.itemCount
      ? `<tr><td colspan="2">${esc(payload.itemCount)} item(s) in order</td></tr>`
      : !payload.items.length
        ? `<tr><td colspan="2">See order details in Admin</td></tr>`
        : "";

  const shipToAddress = formatAddress([
    payload.shipTo.line1,
    payload.shipTo.line2,
    payload.shipTo.city,
    payload.shipTo.state,
    payload.shipTo.zip,
    payload.shipTo.country,
  ]);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>JCGN Package Label — Order #${esc(payload.orderId)}</title>
  <style>
    @page { size: 4in 6in; margin: 0.2in; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
    .sheet { width: 4in; min-height: 6in; padding: 0.18in; border: 2px solid #111; }
    .brand { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 10px; }
    .brand h1 { margin: 0; font-size: 18px; letter-spacing: 0.04em; }
    .badge { border: 2px solid #111; padding: 4px 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; }
    .order-id { font-size: 22px; font-weight: 800; margin: 6px 0 10px; }
    .codes { display: flex; gap: 10px; align-items: center; justify-content: space-between; margin-bottom: 12px; min-height: 90px; }
    .barcode { max-width: 2.4in; height: auto; }
    .qr { width: 96px; height: 96px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
    .box { border: 1px solid #333; padding: 8px; min-height: 1.15in; }
    .box h2 { margin: 0 0 6px; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; }
    .box .name { font-size: 13px; font-weight: 700; margin-bottom: 4px; }
    .box p { margin: 0 0 3px; font-size: 11px; line-height: 1.35; }
    .muted { color: #555; font-size: 10px; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    td { font-size: 11px; padding: 3px 0; border-bottom: 1px dotted #ccc; vertical-align: top; }
    td.qty { width: 36px; text-align: right; font-weight: 700; }
    .footer { margin-top: 10px; border-top: 1px solid #111; padding-top: 8px; font-size: 10px; display: flex; justify-content: space-between; gap: 8px; }
    .otp-note { margin-top: 8px; border: 1px dashed #111; padding: 6px 8px; font-size: 10px; font-weight: 600; }
    .no-print { margin: 12px; font-family: Arial, sans-serif; }
    .no-print button { background: #111; color: #fff; border: 0; padding: 10px 16px; font-size: 14px; cursor: pointer; margin-right: 8px; }
    @media print { .no-print { display: none !important; } body { background: #fff; } }
  </style>
</head>
<body>
  <div class="no-print">
    <button onclick="window.print()">Print label</button>
    <button onclick="window.close()">Close</button>
  </div>
  <div class="sheet">
    <div class="brand">
      <h1>JCGN</h1>
      <div class="badge">${esc(payload.deliveryLabel)}</div>
    </div>
    <div class="order-id">Order #${esc(payload.orderId)}</div>
    <div class="codes">
      <img class="barcode" src="${barcodeDataUrl}" alt="Order barcode ${esc(payload.barcodeValue)}" />
      <img class="qr" src="${qrDataUrl}" alt="Order QR" />
    </div>
    <div class="grid">
      <div class="box">
        <h2>Ship From (Seller)</h2>
        <div class="name">${esc(dash(payload.shipFrom.name))}</div>
        <p>${esc(dash(payload.shipFrom.phone))}</p>
        <p>${esc(dash(payload.shipFrom.location))}</p>
      </div>
      <div class="box">
        <h2>Ship To (Customer)</h2>
        <div class="name">${esc(dash(payload.shipTo.name))}</div>
        <p>${esc(dash(payload.shipTo.phone))}</p>
        <p>${esc(dash(shipToAddress))}</p>
      </div>
    </div>
    <div class="box">
      <h2>Contents (${esc(payload.itemCount || payload.items.length)})</h2>
      <table>
        <tbody>
          ${itemRows || contentsFallback}
          ${moreItems}
        </tbody>
      </table>
    </div>
    <div class="otp-note">
      Delivery confirmation uses OTP in the JCGN Delivery app. Do not write OTP on this label.
    </div>
    <div class="footer">
      <span>Barcode: ${esc(payload.barcodeValue)}</span>
      <span>Printed: ${esc(payload.printedAt)}</span>
    </div>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`;
}

function openPrintDocument(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Package label print");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  if (!frameWindow) {
    document.body.removeChild(iframe);
    throw new Error("Could not open print preview");
  }

  const cleanup = () => {
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  };

  frameWindow.document.open();
  frameWindow.document.write(html);
  frameWindow.document.close();

  const triggerPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch (_) {
      /* ignore */
    }
    cleanup();
  };

  if (frameWindow.document.readyState === "complete") {
    setTimeout(triggerPrint, 300);
  } else {
    frameWindow.addEventListener("load", () => setTimeout(triggerPrint, 300), {
      once: true,
    });
    setTimeout(triggerPrint, 800);
  }
}

export async function printPackageLabel(order, options = {}) {
  const payload = buildPackageLabelPayload(order, options);
  if (!payload.orderId || payload.orderId === "undefined") {
    throw new Error("Order ID is required to print a package label");
  }

  const [barcodeDataUrl, qrDataUrl] = await Promise.all([
    renderBarcodeDataUrl(payload.barcodeValue),
    renderQrDataUrl(payload.qrPayload),
  ]);

  const html = buildLabelHtml(payload, barcodeDataUrl, qrDataUrl);
  openPrintDocument(html);
  return payload;
}
